<template>
  <div class="indicator" :class="{ 'indicator--doughnut': showDoughnut }">
    <div class="f-h f-w indicator__content" @click="clickIndic">
      <!-- Name of the indicator -->
      <div class="indic-name v-centerer" @click.stop="$emit('nameClicked', $event, indicator)">
        <span v-if="healthMode" class="indic-health-dot f-h v-centerer" :class="healthClass">●</span>
        <EllipsisDiv class="v-centerer" :text="indicator.name" />
      </div>

      <!-- Indicator visual : Doughnut or chart -->
      <div v-if="showDoughnut" class="indicator__main-wrapper">

        <div ref="doughnutObjective" class="indicator__main-wrapper__doughnut-obj">
          <div class="indicator__main-wrapper__doughnut-obj__min-max">{{ doughnutObjectiveString }}</div>
          <div class="indicator__main-wrapper__doughnut-obj__limit" :style="limitBarStyle" />
        </div>

        <div class="indicator__main-wrapper__doughnut-wrapper h-v-centerer">
          <div>
            <canvas ref="canvas" class="indicator__canvas" @click="$emit('chartClicked', indicator)" />
          </div>
        </div>

        <!-- Indicator comment if any -->
        <div
          v-if="valueHasComment"
          ref="commentIcon"
          class="indicator__main-wrapper__comment"
          @mouseover="hoveringComment"
          @mouseout="hoveringOutComment"
        >
          <Icon>{{ commentMedium }}</Icon>
        </div>
      </div>

      <div v-else class="indicator__main-wrapper">
        <canvas ref="canvas" class="f-w indicator__canvas" />
      </div>

      <!-- Indicator warning -->
      <SBtn
        v-if="shouldDisplayWarning"
        v-quick-title="indicatorWarningMessage"
        class="indicator-warning"
        color="#d41017"
        round
        floating
        small
        absolute
        @click="displayWarningMessage"
      >
        <img class="indicator-warning-img" src="@/assets/img/warning-white.png" alt="">
      </SBtn>
    </div>

    <!-- Public tag if public -->
    <div
      v-if="indicator.copyPublic"
      v-quick-title="$t('message.INDIC_PUBLIC_FROM_OTHER_TEAM')"
      class="public-title"
    >
      {{ $t('message.SETTINGS_PUBLIC') }}
    </div>

    <!-- Indicator message if the wrong period is selected -->
    <div v-if="wrongPointPeriod" class="wrong-period-message">
      <div class="wrong-period-content">
        <EllipsisDiv :key="wpKey" class="field-hint" :text="wrongPeriodText" />
        <button
          class="generic-border generic-border--small-text-btn"
          @click="$emit('changePeriod', $event, indicator.inputPeriodUnit)"
        >
          <div>{{ switchPeriodButtonText }}</div>
        </button>
      </div>
    </div>
    <LoadingOverlay v-model="refreshing" />
  </div>
</template>

<script>
/**
 * Indicator item displaying a chart
 *
 * @vue-prop {Object} indicator - The indicator object from the DB
 * @vue-prop {Object} range - Range of values. May include values not visible due to the user-defined period.
 *                            Invisible values must be included in case thecustomConfig cumulates values.
 *                            eg: Values should be cumulated each month from the 1st of each month
 *                                => need the values from the 1st day of month
 *                            Must contains `start` and `end` Moment instances
 * @vue-prop {Object} visibleChartRange - Range of <b>visible</b> values in the chart. Same as above.
 * @vue-prop {Object} [customConfig] - Values display configuration as expected in ChartHelper.buildCustomConfigChart
 * @vue-prop {Boolean} [cumulated] - Whether this indicator is a cumulated library indicator
 * @vue-prop {Boolean} [disabled] - Whether the click event should be listened to or not
 * @vue-prop {Boolean} [refreshing] - If true, display a loading overlay
 * @vue-prop {Array} [teamIds] - When cumulated is true, aray of team ids to filter indicators to cumulate.
 *                               If cumulated is true and this array is undefined, use the values of the
 *                               cumulated indicator instead
 * @vue-prop {Array} [workingDays] - User working days array
 */

import $ from 'jquery';
import { Chart } from 'chart.js';
import Moment from 'moment-timezone';
import { isEqual } from 'lodash';
// Components
import EllipsisDiv from '@/components/ellipsize.vue';
import Icon from '@/components/icon.vue';
import LoadingOverlay from '@/components/loading-overlay.vue';
import SBtn from '@/components/s-btn.vue';
// Mixins
import mixinCommonColors from '@/mixins/common-colors.vue';
// Utils
import {
  ChartHelper,
  doesIndicatorMatchConfig,
  valueKeyFormat,
  parsePointTooltipHtml,
} from '@/utils/indicator-utils';
import { commentMedium } from '@/assets/icons';
import { calert } from '@/utils/main';
import { SColors } from '@/utils/constants';
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';


export default {

  name: 'Indicator',

  components: {
    EllipsisDiv,
    Icon,
    LoadingOverlay,
    SBtn,
  },

  mixins: [mixinCommonColors],

  props: {
    indicator: {
      type: Object,
      required: true,
    },
    range: {
      type: Object,
      validator(value) {
        return value instanceof Object
          && (Moment.isMoment(value.start) && value.start.isValid())
          && (Moment.isMoment(value.end) && value.end.isValid());
      },
      required: true,
    },
    visibleChartRange: {
      type: Object,
      validator(value) {
        return value instanceof Object
          && (Moment.isMoment(value.start) && value.start.isValid())
          && (Moment.isMoment(value.end) && value.end.isValid());
      },
      default: null,
    },
    customConfig: {
      type: Object,
      default: () => ({ pointPeriodObj: { period: 1, unit: 'd' } }),
    },
    cumulated: Boolean,
    disabled: Boolean,
    refreshing: Boolean,
    teamIds: {
      type: Array,
      default: undefined,
    },
    workingDays: {
      type: Array,
      default: undefined,
    },
  },

  data() {
    return {
      commentMedium,
      chart: null,
      chartHelper: null,
      chartConfig: null,
      wpKey: 1,
      buildTimeout: null,
      // Doughnut thickness as a percentage of the chart div
      doughnutChartThickness: 8,
      doughnutChartSize: 0,
    };
  },

  computed: {
    shouldDisplayWarning() {
      if (this.cumulated) {
        return false;
      }
      if (this.indicatorWarning) {
        const warning = this.indicatorWarning;
        const wd = this.workingDays || this.currentTeam.workingDays || [true, true, true, true, true, true, true];
        const now = this.$moment(this.range.end);
        const oIsMax = Number(this.indicator.objectiveIsMax) === 1;
        let nDaysChecked = 0;
        while (nDaysChecked < warning.nDays) {
          if (wd[now.isoWeekday() - 1]) {
            const inputVal = this.inputValues[now.format(valueKeyFormat)];
            if (inputVal) {
              const val = inputVal.value;
              if (oIsMax && (val - this.indicator.objective > warning.gap)) return true;
              if (!oIsMax && (this.indicator.objective - val > warning.gap)) return true;
            }
            nDaysChecked++;
          }
          now.subtract(1, 'd');
        }
      }
      return false;
    },
    /** Boolean: Display doughnut if the point period is the same as the chart period */
    showDoughnut() {
      const cp = this.customConfig.chartPeriodObj;
      const pp = this.customConfig.pointPeriodObj;
      return pp.period === cp.period && pp.unit === cp.unit;
    },
    /** Number: Objective of the doughnut's focused date */
    doughnutObjective() {
      const chartPoint = this.chartDataPoints[this.chartDataLength - 1];
      const obj = chartPoint ? chartPoint.objectiveData : null;
      return obj ? Number(obj).sToFixed(2) : obj;
    },
    /** String: Objective displayed above the doughnut */
    doughnutObjectiveString() {
      return Number(this.indicator.objectiveIsMax) === 1
        ? this.$t('message.MAX_OBJECTIVE', { objective: this.doughnutObjective })
        : this.$t('message.MIN_OBJECTIVE', { objective: this.doughnutObjective });
    },
    /** Object: Style of the objective bar */
    limitBarStyle() {
      let topMargin = 0;
      if (this.$refs.canvas && this.$refs.doughnutObjective) {
        const canvasRect = this.$refs.canvas.getBoundingClientRect();
        const objRect = this.$refs.doughnutObjective.getBoundingClientRect();
        topMargin = canvasRect.top - (objRect.top + objRect.height);
      }
      return {
        height: `${this.doughnutPxThickness * 1.5 + topMargin}px`,
      };
    },
    /** Doughnut chart thickness in pixels */
    doughnutPxThickness() {
      return this.doughnutChartSize * this.doughnutChartThickness / 100;
    },
    chartDataPoints() {
      return (this.chartConfig && this.chartConfig.points) || [];
    },
    chartDataLength() {
      return this.chartDataPoints.length;
    },
    /** Object: Value of the doughnut's focused date { value: <numeric_val>, text: <value_and_unit> } */
    doughnutCurrentValue() {
      const chartPoint = this.chartDataPoints[this.chartDataLength - 1];
      if (!chartPoint) {
        return { value: null, text: '-' };
      }
      return {
        value: chartPoint.data === null ? null : Number(chartPoint.data),
        text: `${Number(chartPoint.data).sToFixed(2)}${this.indicator.unit === '' ? '' : ` ${this.indicator.unit}`}`,
      };
    },
    /** Boolean: Whether the current doughnut value overruns the objective */
    doughnutCurrentOverrun() {
      const objective = Number(this.doughnutObjective);
      const { value } = this.doughnutCurrentValue;
      return value !== objective && (value > objective) === (Number(this.indicator.objectiveIsMax) === 1);
    },
    /** Boolean: Whether the current doughnut's value features a comment or not */
    valueHasComment() {
      const chartPoint = this.chartDataPoints[this.chartDataLength - 1];
      return chartPoint && chartPoint.tooltip.afterBody && chartPoint.tooltip.afterBody.length;
    },
    /** String: Title of the comment as displayed in doughnut's tooltip */
    commentTitle() {
      const { tooltip: { title } } = this.chartDataPoints[this.chartDataLength - 1];
      return title;
    },
    /** String: Comment of the doughnut's focused date */
    commentBody() {
      return parsePointTooltipHtml(this.chartDataPoints[this.chartDataLength - 1].tooltip, '', true);
    },
    /** Indicator warning data */
    indicatorWarning() {
      return this.$store.state.indicatorWarnings[`${this.indicator.id}`];
    },
    indicatorWarningMessage() {
      return this.indicatorWarning ? this.indicatorWarning.message : '';
    },
    /** Display configuration of the chart */
    chartConfiguration() {
      const { pointPeriodObj } = this.customConfig;
      return {
        ...this.customConfig,
        pointPeriod: pointPeriodObj.period,
        pointUnit: pointPeriodObj.unit,
        pointStartDay: pointPeriodObj.startDay,
        configStart: this.range.start.clone(),
      };
    },
    /** Object: Indicator data from state */
    stateIndicator() {
      const key = this.cumulated ? 'cumulatedIndicators' : 'indicators';
      return this.$store.state[key][`${this.indicator.id}`] || this.indicator;
    },
    /** Values to be displayed in charts and editable through indicator-input */
    inputValues() {
      return this.stateIndicator.inputValues || {};
    },
    /** Should the indicator be displayed according to the period unit */
    wrongPointPeriod() {
      return !doesIndicatorMatchConfig(this.customConfig.pointPeriodObj.unit, this.indicator);
    },
    /** Text to display if wrongPointPeriod is true */
    wrongPeriodText() {
      const what = this.$tc(`message.${this.indicator.inputPeriodUnit === 'w' ? 'WEEKLY' : 'MONTHLY_FEM'}`, 2);
      return this.$t('message.INDIC_DISPLAY_WRONG_PERIOD', { what: what.toLowerCase() });
    },
    /** Text to display on the button if wrongPointPeriod is true */
    switchPeriodButtonText() {
      const what = this.$tc(`message.${this.indicator.inputPeriodUnit === 'w' ? 'WEEKLY' : 'MONTHLY'}`, 2);
      return this.$t('message.CHANGE_DISPLAY_INDIC_PERIOD', { what: what.toLowerCase() });
    },
    /** Boolean: Whether this is a calculated indicator */
    isCalculated() {
      return this.indicator.calcItems && this.indicator.calcItems.length > 0;
    },
    /** Boolean: Whether to show a health dot next to the chart */
    healthMode() {
      const { health } = this.stateIndicator;
      return health && !isNaN(health.id) && health.id >= 0 && health.id <= 3;
    },
    /** String: Class applied to the health dot */
    healthClass() {
      if (this.healthMode) {
        return this.stateIndicator.health.color;
      }
      return '';
    },
  },

  watch: {
    indicator: {
      handler() {
        this.build();
      },
      deep: true,
    },
    inputValues(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.build();
      }
    },
    chartConfiguration(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.build();
      }
    },
  },

  mounted() {
    this.chartHelper = new ChartHelper();
    this.build();
    $(window).on(this.resizeEventName, this.build);
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
    clearTimeout(this.buildTimeout);
  },

  methods: {
    resizeChart() {
      this.wpKey++;
      if (this.showDoughnut) {
        this.buildChart();
      } else if (this.chart) {
        this.chart.resize();
      } else if (this.$refs.canvas) {
        const { canvas } = this.$refs;
        // If there is no chart, we resize manually the canvas to not have to call
        // chart.resize after the chart creation, avoiding a unecessary re-render
        const { clientHeight, clientWidth } = canvas.parentElement;
        canvas.height = clientHeight;
        canvas.width = clientWidth;
        canvas.style.height = `${clientHeight}px`;
        canvas.style.width = `${clientWidth}px`;
      }
    },
    clickIndic() {
      if (!this.disabled) {
        this.$router.push(`/indicator?detailedIndicatorId=${this.indicator.id}`);
      } else {
        this.$emit('chartClicked');
      }
    },
    build() {
      // Prevent repeated build
      clearTimeout(this.buildTimeout);
      this.buildTimeout = setTimeout(this.doBuild, 150);
    },
    doBuild() {
      // Build inputValues
      if (!this.$refs.canvas) return;
      // When cumulating indicators, fetch indicators of teams filtered by teamIds prop
      const indicators = !this.cumulated || !this.teamIds
        ? undefined
        : (this.stateIndicator.teamIndicators || [])
          .map(id => this.$store.state.indicators[`${id}`])
          .filter(i => i !== undefined && this.teamIds.includes(Number(i.team)));
      this.chartConfig = this.chartHelper.buildCustomConfigChart(
        this.stateIndicator,
        this.inputValues,
        this.chartConfiguration,
        this.workingDays || this.currentTeam.workingDays,
        this.visibleChartRange || this.range,
        indicators,
      );
      this.buildChart();
    },
    buildChart() {
      if (this.chart) {
        this.chart.destroy();
        delete this.chart;
      }
      const { canvas } = this.$refs;
      if (this.showDoughnut) {
        const {
          indicatorsGreenBg,
          indicatorsRedBg,
          indicatorsGreenLine,
          indicatorsRedLine,
        } = SColors;

        const objective = Number(this.doughnutObjective);
        // const max = objective !== 0 ? objective * 2 : 1;
        const max = objective !== 0 ? objective : 1;
        const val = Object.assign({}, this.doughnutCurrentValue);
        if (this.doughnutCurrentValue.value === null) {
          val.value = 0;
          val.bgColor = this.doughnutBgColor;
          val.fgColor = this.doughnutFgGray;
        }
        let data = val.value > max ? [max, 0] : [val.value, max - val.value];
        if (data[0] === 0 && data[1] === 0) {
          data = [0, 1];
        }
        if (val.bgColor === undefined) {
          val.bgColor = this.doughnutCurrentOverrun ? indicatorsRedBg : indicatorsGreenBg;
          val.fgColor = this.doughnutCurrentOverrun ? indicatorsRedLine : indicatorsGreenLine;
        }
        const config = {
          responsive: false,
          maintainAspectRatio: false,
          type: 'doughnut',
          data: {
            labels: [this.indicator.name],
            datasets: [{
              data,
              backgroundColor: [val.fgColor, val.bgColor],
              hoverBackgroundColor: [val.fgColor, val.bgColor],
              borderWidth: [0, 0],
              hoverBorderWidth: [0, 0],
            }],
          },
          options: {
            cutout: `${100 - this.doughnutChartThickness * 2}%`,
            elements: {
              arc: {
                roundedCornersFor: { start: [1], end: [0, 1] },
              },
              center: {
                text: this.doughnutCurrentValue.text,
                color: val.fgColor,
                fontStyle: 'Work Sans',
                maxFontSize: 24,
              },
            },
          },
        };
        this.chart = new Chart(canvas.getContext('2d'), config);
        this.doughnutChartSize = Math.min(canvas.offsetWidth, canvas.offsetHeight);
      } else {
        this.chart = this.chartHelper.drawChart(this.indicator, this.chartConfig, canvas, undefined, true);
      }
    },
    /** Display the fullscreen warning message */
    displayWarningMessage() {
      if (this.shouldDisplayWarning) {
        calert(this.$t('message.IW_YOUVE_SETUP', { msg: this.indicatorWarningMessage }), [
          { title: this.$t('message.OK') },
        ]);
      }
    },
    /** Display the indicator tooltip for doughnuts */
    hoveringComment() {
      if (this.$refs.commentIcon) {
        const elt = this.$refs.commentIcon;
        const rect = elt.getBoundingClientRect();
        const width = 300;
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_SHOW, {
          tooltip: {
            title: this.commentTitle,
            body: this.commentBody,
          },
          rect: {
            bottom: windowHeight - rect.top,
            right: rect.left > windowWidth - width ? 0 : windowWidth - (rect.left + width),
            width,
          },
        });
        const clParams = { clickable: true, canvas: this.$refs.commentIcon };
        globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_SET_CLICKABLE, clParams);
      }
    },
    /** Hide the indicator tooltip */
    hoveringOutComment() {
      const clParams = { clickable: false, canvas: this.$refs.commentIcon };
      globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_SET_CLICKABLE, clParams);
      globalEventDispatcher.dispatch(ShizenGlobalEvent.CHART_TOOLTIP_HIDE);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.indicators-wrapper .indicator.editing {
  padding-bottom: 10px;
}
.indicator .indicator-warning {
  top: 0;
  right: 0;
  z-index: auto;
}
.indicator-warning-img {
  width: 20px;
  height: 20px;
}
.indicator {
  $indicator-name-height: 2.6rem;

  .indic-name {
    cursor: pointer;
    white-space: normal;
    width: 100%;

    div {
      font-size: 0.8em;
      line-height: 1.3em;
      height: $indicator-name-height;
      font-weight: 600;
    }

    .indic-health-dot {
      font-size: 1em;
      margin-right: 5px;

      &.green { color: $color-green-good; }
      &.yellow { color: $color-yellow; }
      &.orange { color: $color-orange; }
      &.red { color: $color-red-bad; }
    }
  }

  &__main-wrapper {
    width: 100%;
    height: calc(100% - #{$indicator-name-height});
    box-sizing: border-box;
  }

  &--doughnut .indicator__main-wrapper {
    width: 88%;
    margin-left: 6%;
    padding-top: .7rem;

    .indicator__canvas {
      width: auto;
      height: auto;
    }
  }

  &__canvas {
    width: 100%;
    height: 100%;
  }

  .public-title {
    position: absolute;
    bottom: 0px;
    transform: rotate(-45deg) translate(50%, -100%);
    right: 5px;
    background: #ced0ff;
    padding: 2px 5px;
    font-weight: 600;
    font-size: 0.8em;
    border-radius: 30px;
    border: 2px solid #6b6da2;
  }

  .wrong-period-message {
    width: 100%;
    position: absolute;
    top: 3rem;
    left: 0px;
    bottom: 0px;
    background: rgba(255, 255, 255, 0.7);

    .wrong-period-content {
      position: absolute;
      top: 50%;
      left: 0px;
      transform: translateY(-50%);
      width: 100%;
      padding: 5px;
      box-sizing: border-box;
      text-align: center;

      .field-hint {
        height: 3.9rem;
        line-height: 1.3rem;
        padding: 0 10%;
      }

      button {
        margin: .3rem auto;
        max-width: 100%;
        overflow: hidden;

        > div {
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }
  }

  &--doughnut {

    .indicator__main-wrapper {

      $bracket-width: 8px;
      $bracket-thickness: 3px;
      $objective-val-height: 1.5em;

      position: relative;

      canvas {
        max-height: 100%;
        cursor: pointer;
      }

      &__doughnut-obj {
        position: relative;
        height: 1.5rem;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;

        &__min-max {
          border-radius: 4px;
          padding: .2rem .5rem;
          font-size: 0.9rem;
          color: $color-shizen-gray-text-light;
        }

        &__limit {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          width: 1px;
          min-height: 1.2rem;
          background: linear-gradient(to bottom, transparent 50%, $color-shizen-blue 50%);
          background-size: 1px 8px, 2px 100%;
        }
      }

      &__doughnut-wrapper {
        width: 100%;
        // Height of the objective + margin
        height: calc(100% - 2rem);
        margin: 0 auto 0rem auto;

        > div {
          flex: 1;
          max-width: 100%;
          max-height: 100%;
        }
      }

      &__comment {
        position: absolute;
        box-sizing: border-box;
        right: -10%;
        bottom: .2rem;
        padding: 0.5rem;
        width: 2.3rem;
        height: 2.3rem;

        .icon {
          display: block;
          width: 100%;
          height: 100%;
        }
      }
    }
  }
}
</style>
